import { useState, FormEvent } from 'react'
import { submitContact } from '../services/contactService'
import { ApiServiceError } from '../services/baseApiService'
import './ContactPage.css'

function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!formData.name.trim()) errs.name = 'Name is required'
    if (!formData.email.trim()) {
      errs.email = 'Email is required'
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errs.email = 'Please enter a valid email address'
    }
    if (!formData.message.trim()) errs.message = 'Message is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n })
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    setSubmitError('')
    try {
      await submitContact({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      })
      setSubmitted(true)
    } catch (error) {
      const message = error instanceof ApiServiceError
        ? error.message
        : 'Something went wrong. Please try again.'
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="contact-page">
        <div className="contact-success">
          <div className="contact-success-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2>Thank you for your message!</h2>
          <p>We'll get back to you within 5-10 days.</p>
          <button className="btn btn-primary" onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', message: '' }); setSubmitError('') }}>
            Send Another Message
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <h1>Get in Touch</h1>
        <p>Whether you're a researcher seeking precise peptide purity analysis, or exploring how we can support your work, our team is ready to help.</p>
      </div>

      <div className="contact-grid">
        <div className="contact-info-panel">
          <div className="contact-info-card">
            <div className="contact-info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div>
              <h4>Our Location</h4>
              <p>Unit 12, Skyport Dr<br />West Drayton UB7 0LB<br />United Kingdom</p>
            </div>
          </div>

          <div className="contact-info-card">
            <div className="contact-info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div>
              <h4>Email</h4>
              <p>info@hiltonanalytica.co.za</p>
            </div>
          </div>

          <div className="contact-info-card">
            <div className="contact-info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <h4>Turnaround</h4>
              <p>Results in 5-10 days</p>
            </div>
          </div>

          <div className="contact-help-topics">
            <h4>How Can We Help?</h4>
            <ul>
              <li>Testing services & pricing</li>
              <li>Sample submission process</li>
              <li>COA lookup assistance</li>
              <li>Research project inquiries</li>
            </ul>
          </div>
        </div>

        <div className="contact-form-panel">
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange('name')}
                placeholder="Your full name"
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder="you@company.com"
                required
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                value={formData.message}
                onChange={handleChange('message')}
                placeholder="Tell us how we can help you..."
                rows={6}
              />
              {errors.message && <span className="field-error">{errors.message}</span>}
            </div>
            <button type="submit" className="btn btn-primary btn-submit" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitError && <p className="contact-error">{submitError}</p>}
            <p className="contact-form-hint">
              Prefer to check on an existing request?{' '}
              <a href="/query">Submit a COA query instead</a>.
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ContactPage
